import { z } from 'zod';

export enum EquipmentTypes {
    Van = 'VAN',
    Reefer = 'REEFER',
    FlatBed = 'FLATBED',
}

export const MciGaugeQueryPayloadSchema = z.object({
    id: z.string(),
    score: z.number(),
    fetch: z.boolean().default(false),
    loaded: z.boolean().default(false),
    hasError: z.boolean().default(false),
    noData: z.boolean().default(false),
    capacity: z.string().nullable(),
    geoCode: z.string().or(z.number()).nullable(),
    capacityMap: z
        .record(
            z.string(),
            z.object({
                high: z.number(),
                low: z.number(),
                color: z.string().nullable(),
                index: z.number(),
            }),
        )
        .default({}),
});

export const EquipmentTypeChangePayloadSchema = z.object({
    id: z.string(),
    query: z.object({
        id: z.string(),
        equipmentType: z.nativeEnum(EquipmentTypes),
    }),
});

export const UpdateMcQueryControlsPayloadSchema = z.object({
    direction: z.string(),
    displayName: z.string().nullable(),
    geoCode: z
        .union([z.string(), z.number(), z.null()])
        .transform((val) => (val === null ? '' : String(val)))
        .default(''),
    equipmentType: z.nativeEnum(EquipmentTypes),
    timeframe: z.string(),
    geoType: z.string(),
});

export const SetMarketDetailsLabelPayloadSchema = z.object({
    name: z.string().nullable(),
});

export type MciGaugeQueryPayload = z.infer<typeof MciGaugeQueryPayloadSchema>;

export type EquipmentTypeChangePayload = z.infer<
    typeof EquipmentTypeChangePayloadSchema
>;

export type UpdateMcQueryControlsPayload = z.infer<
    typeof UpdateMcQueryControlsPayloadSchema
>;

export type SetMarketDetailsLabelPayload = z.infer<
    typeof SetMarketDetailsLabelPayloadSchema
>;

export const CapacityMaps = {
    [EquipmentTypes.Van]: {
        'Very Loose': {
            high: -36,
            low: -100,
            color: '#1053C7',
            index: 0,
        },
        'Moderately Loose': {
            high: -31,
            low: -35,
            color: '#4AA6D2',
            index: 1,
        },
        'Slightly Loose': {
            high: -21,
            low: -30,
            color: '#ADE6D0',
            index: 2,
        },
        'Neutral Market': {
            high: 9,
            low: -20,
            color: '#FFF6AD',
            index: 3,
        },
        'Slightly Tight': {
            high: 34,
            low: 10,
            color: '#FFCCA8',
            index: 4,
        },
        'Moderately Tight': {
            high: 44,
            low: 35,
            color: '#F78B5E',
            index: 5,
        },
        'Very Tight': {
            high: 100,
            low: 45,
            color: '#C60B1F',
            index: 6,
        },
    },
    [EquipmentTypes.Reefer]: {
        'Very Loose': {
            high: -37,
            low: -100,
            color: '#1053C7',
            index: 0,
        },
        'Moderately Loose': {
            high: -26,
            low: -36,
            color: '#4AA6D2',
            index: 1,
        },
        'Slightly Loose': {
            high: -16,
            low: -25,
            color: '#ADE6D0',
            index: 2,
        },
        'Neutral Market': {
            high: 9,
            low: -15,
            color: '#FFF6AD',
            index: 3,
        },
        'Slightly Tight': {
            high: 42,
            low: 10,
            color: '#FFCCA8',
            index: 4,
        },
        'Moderately Tight': {
            high: 84,
            low: 43,
            color: '#F78B5E',
            index: 5,
        },
        'Very Tight': {
            high: 100,
            low: 85,
            color: '#C60B1F',
            index: 6,
        },
    },
    [EquipmentTypes.FlatBed]: {
        'Very Loose': {
            high: -37,
            low: -100,
            color: '#1053C7',
            index: 0,
        },
        'Moderately Loose': {
            high: -26,
            low: -36,
            color: '#4AA6D2',
            index: 1,
        },
        'Slightly Loose': {
            high: -16,
            low: -25,
            color: '#ADE6D0',
            index: 2,
        },
        'Neutral Market': {
            high: 9,
            low: -15,
            color: '#FFF6AD',
            index: 3,
        },
        'Slightly Tight': {
            high: 42,
            low: 10,
            color: '#FFCCA8',
            index: 4,
        },
        'Moderately Tight': {
            high: 84,
            low: 43,
            color: '#F78B5E',
            index: 5,
        },
        'Very Tight': {
            high: 100,
            low: 85,
            color: '#C60B1F',
            index: 6,
        },
    },
};

export const CapacityLevelsSchema = z.union([
    z.literal('Very Loose'),
    z.literal('Moderately Loose'),
    z.literal('Slightly Loose'),
    z.literal('Neutral Market'),
    z.literal('Slightly Tight'),
    z.literal('Moderately Tight'),
    z.literal('Very Tight'),
]);
export type CapacityLevels = z.infer<typeof CapacityLevelsSchema>;

export interface IIqMcGaugeStateService {}

export const IqMciUpdatingMsg =
    'MCI Forecasting is updating and will be available soon.';

export const McLocationQueryTypeSchema = z.union([
    z.literal('location'),
    z.literal('area'),
]);

export enum Directions {
    InBound = 'IN',
    OutBound = 'OUT',
}

const DirectionSchema = z.nativeEnum(Directions);

export enum GeoTypes {
    Country = 'COUNTRY',
    Region = 'REGION',
    State = 'STATE',
    ExtendedMarket = 'X_MKT',
    Market = 'MKT',
    ThreeDZ = 'THREEDZ',
}

const GeoTypesSchema = z.nativeEnum(GeoTypes);

export const EquipmentTypeSchema = z.union([
    z.nativeEnum(EquipmentTypes),
    z.literal(''),
]);

export enum TimeFrames {
    ThirtyDaysBack = 'THIRTY_DAYS_BACK',
    EightDaysBack = 'EIGHT_DAYS_BACK',
    PrevBusinessDay = 'PREV_BUSINESS_DAY',
    Current = 'CURRENT',
    EightDayForecast = 'EIGHT_DAY_FORECAST',
}

const TimeFrameSchema = z.nativeEnum(TimeFrames);

export const McQuerySchema = z.object({
    id: z.string(),
    type: McLocationQueryTypeSchema,
    direction: DirectionSchema,
    geoType: GeoTypesSchema,
    geoCode: z
        .union([z.string(), z.number(), z.null()])
        .transform((val) => (val === null ? '' : String(val)))
        .default(''),
    equipmentType: EquipmentTypeSchema,
    timeframe: TimeFrameSchema.default(TimeFrames.Current),
    displayName: z.string().or(z.null()).default(''),
    date: z.string().or(z.null()).default(''),
});

export type McQuery = z.infer<typeof McQuerySchema>;
